import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, NgModule, Optional, Inject } from '@angular/core';
import { createEmptyDashboard } from '@sick-curie/dashboard-builder-models';
import { dashboardElements } from '@sick-curie/dashboard-builder-ui-models';
import { mergeWith, isArray } from 'lodash-es';
import { Subject } from 'rxjs';

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class DashboardStructureService {
    constructor() {
        this.structureChanged$ = new Subject();
        this.root = {
            type: 'root',
            element: undefined,
            children: [],
        };
    }
    static traverse(startNode, fun) {
        fun(startNode);
        const children = startNode.type === 'grid' ? [startNode.child] : [...startNode.children];
        children.forEach((c) => DashboardStructureService.traverse(c, fun));
    }
    findParent(node, startNode) {
        if (!startNode) {
            startNode = this.root;
        }
        const children = startNode.type === 'grid' ? [startNode.child] : startNode.children;
        if (children.some((child) => child === node)) {
            return startNode;
        }
        for (const child of children) {
            const result = this.findParent(node, child);
            if (result) {
                return result;
            }
        }
        return undefined;
    }
    get structureChanged() {
        return this.structureChanged$;
    }
    addElementTo(parent, element) {
        const node = {
            type: 'element',
            element,
            children: [],
        };
        parent.children = parent.children.concat(node);
        this.structureChanged$.next();
        return node;
    }
    addToRoot(node) {
        this.root.children = this.root.children.concat(node);
        this.structureChanged$.next();
    }
    removeNode(node) {
        const parent = this.findParent(node);
        if (!parent) {
            return;
        }
        if (parent.type === 'grid') {
            throw new Error('Cannot delete the content of a grid node without deleting the grid node itself');
        }
        const index = parent.children.indexOf(node);
        if (index >= 0) {
            parent.children =
                parent.type === 'root'
                    ? parent.children.filter((_, i) => i !== index)
                    : parent.children.filter((_, i) => i !== index);
        }
        this.structureChanged$.next();
    }
    modifyElement(node, change) {
        node.element = mergeWith({}, node.element, change, (objValue, srcValue) => {
            if (isArray(objValue)) {
                return [...srcValue];
            }
            return undefined;
        });
        this.structureChanged$.next();
    }
    setRootNode(node) {
        DashboardStructureService.traverse(node, (n) => {
            if (n.element?.tag && !dashboardElements.getDescription(n.element?.tag)) {
                throw new Error('Unknown element ' + n.element.tag);
            }
        });
        this.root = node;
        this.structureChanged$.next();
    }
    getRootNode() {
        return this.root;
    }
    getElementsByTag(tag) {
        const foundElements = [];
        DashboardStructureService.traverse(this.root, (n) => {
            if (n.type === 'element' && n.element.tag === tag) {
                foundElements.push(n);
            }
        });
        return foundElements;
    }
}
DashboardStructureService.EMPTY = createEmptyDashboard();
DashboardStructureService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardStructureService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DashboardStructureService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardStructureService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardStructureService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class DashboardBuilderServicesModule {
}
DashboardBuilderServicesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderServicesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderServicesModule, imports: [CommonModule] });
DashboardBuilderServicesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderServicesModule, providers: [DashboardStructureService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderServicesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [DashboardStructureService],
                }]
        }] });

class DashboardManagementService {
    constructor(structureService, bindingMiddleware) {
        this.structureService = structureService;
        this.bindingMiddleware = 'dashboard-data-converter';
        if (bindingMiddleware) {
            this.bindingMiddleware = bindingMiddleware;
        }
    }
    addTopLevelElement(element) {
        const elementNode = {
            type: 'element',
            element,
            children: [],
        };
        const gridNode = {
            type: 'grid',
            element: {
                tag: 'dashboard-grid-tile',
                properties: {
                    header: dashboardElements.getDescription(element.tag).documentation.name,
                    lock: false,
                },
            },
            child: elementNode,
        };
        this.structureService.addToRoot(gridNode);
        return gridNode;
    }
    addBinding(node, bindingTemplate, property) {
        const binding = JSON.parse(JSON.stringify(bindingTemplate));
        const nodeToAddBindingTo = node.type === 'grid' ? node.child : node;
        let converter = nodeToAddBindingTo.children.find((child) => child.element.tag === this.bindingMiddleware && child.element.properties.property === property.name);
        if (!converter) {
            const propertyIsArray = property.index !== undefined;
            const converterElement = {
                tag: this.bindingMiddleware,
                properties: {
                    property: property.name,
                    propertyIsArray,
                },
            };
            converter = this.structureService.addElementTo(nodeToAddBindingTo, converterElement);
        }
        binding.properties.property = `value${property.index ?? 0}`;
        return this.structureService.addElementTo(converter, binding);
    }
    removeBindings(node) {
        node.children
            .filter((child) => child.element.tag === this.bindingMiddleware)
            .forEach((converter) => this.structureService.removeNode(converter));
    }
    getBindings(node) {
        return node.children
            .filter((child) => child.element.tag === this.bindingMiddleware)
            .map((converter) => converter.children.map((binding) => {
            const propertyName = binding.element.properties.property;
            const index = Number(propertyName.substr('value'.length));
            return {
                property: {
                    name: converter.element.properties.property,
                    ...(converter.element.properties.propertyIsArray ? { index } : {}),
                },
                instance: binding.element,
            };
        }))
            .reduce((acc, cur) => acc.concat(cur), []);
    }
    setBindings(node, bindings) {
        this.removeBindings(node);
        bindings.forEach((binding) => this.addBinding(node, binding.instance, binding.property));
    }
}
DashboardManagementService.BINDING_MIDDLEWARE_TOKEN = 'BindingMiddleWareToken';
DashboardManagementService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardManagementService, deps: [{ token: DashboardStructureService }, { token: DashboardManagementService.BINDING_MIDDLEWARE_TOKEN, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
DashboardManagementService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardManagementService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardManagementService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: DashboardStructureService }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DashboardManagementService.BINDING_MIDDLEWARE_TOKEN]
                }] }]; } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class DashboardElementValidationService {
    validate(element, elementDefinition) {
        if (!element || !elementDefinition) {
            return [
                {
                    type: 'error',
                    msg: 'Element and ElementDefinition should be defined',
                },
            ];
        }
        if (typeof element.properties === 'object') {
            return Object.keys(element.properties)
                .map((propertyName) => {
                const propertyDefinition = elementDefinition.properties.find((property) => property.name === propertyName);
                return this._validateProperty(element, propertyName, element.properties[propertyName], propertyDefinition);
            })
                .filter((result) => result.type === 'error' || result.type === 'warning');
        }
        else {
            return [
                {
                    type: 'error',
                    msg: `Element should contain property 'properties'`,
                },
            ];
        }
    }
    _validateProperty(element, propertyName, propertyValue, propertyDefinition) {
        if (!propertyDefinition) {
            return {
                type: 'error',
                fix: {
                    action: 'remove',
                    element,
                    property: propertyName,
                },
                msg: `Property not supported: ${propertyName}`,
            };
        }
        if (propertyDefinition.type === 'string' ||
            propertyDefinition.type === 'boolean' ||
            propertyDefinition.type === 'number' ||
            propertyDefinition.type === 'unknown') {
            return this._validateBasicPropertyType(propertyName, propertyValue, propertyDefinition.type);
        }
        else if (propertyDefinition.type === 'string[]' ||
            propertyDefinition.type === 'Array<string>' ||
            propertyDefinition.type === 'boolean[]' ||
            propertyDefinition.type === 'Array<boolean>' ||
            propertyDefinition.type === 'number[]' ||
            propertyDefinition.type === 'Array<number>' ||
            propertyDefinition.type === 'unknown[]' ||
            propertyDefinition.type === 'Array<unknown>') {
            return this._validateArrayPropertyType(propertyName, propertyValue, propertyDefinition.type);
        }
        else {
            // validation not possible
            return {
                type: 'warning',
                msg: `Can not validate - Tag: ${element.tag}, Property: ${propertyName}, Type: ${propertyDefinition.type}, Value: ${propertyValue}`,
            };
        }
        return {
            type: 'success',
        };
    }
    _validateArrayPropertyType(propertyName, propertyValue, expectedType) {
        const propertyType = typeof propertyValue;
        if (propertyType !== 'object' && !Array.isArray(propertyValue)) {
            return {
                type: 'error',
                msg: `Type error for ${propertyName} - expected '${expectedType}' but got ${propertyType}`,
            };
        }
        else {
            return {
                type: 'success',
            };
        }
    }
    _validateBasicPropertyType(propertyName, propertyValue, expectedType) {
        const propertyType = typeof propertyValue;
        if (expectedType === 'unknown') {
            return {
                type: 'success',
            };
        }
        else if (propertyType !== expectedType) {
            return {
                type: 'error',
                msg: `Type error for ${propertyName} - expected '${expectedType}' but got ${propertyType}`,
            };
        }
        else {
            return {
                type: 'success',
            };
        }
    }
}
DashboardElementValidationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardElementValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
DashboardElementValidationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardElementValidationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardElementValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class DashboardSanitizerService {
    constructor(dashboardElementValidationService) {
        this.dashboardElementValidationService = dashboardElementValidationService;
    }
    static traverse(startNode, fun) {
        fun(startNode);
        const children = startNode.type === 'grid' ? [startNode.child] : [...startNode.children];
        children.forEach((c) => DashboardSanitizerService.traverse(c, fun));
    }
    checkRootNode(node) {
        let result = [];
        DashboardSanitizerService.traverse(node, (n) => {
            result = result.concat(this._validateNode(n));
        });
        return result;
    }
    applyValidation(validation) {
        if (validation.length !== 0) {
            // eslint-disable-next-line no-console
            console.warn('Apply validation', validation);
        }
        this._handleValidationErrorResults(validation);
    }
    _handleValidationErrorResults(validationResults) {
        validationResults
            .filter((validationResult) => validationResult.type === 'error')
            .forEach((validationResult) => {
            if (validationResult.fix) {
                const fix = validationResult.fix;
                switch (fix.action) {
                    case 'remove':
                        delete fix.element.properties[fix.property];
                        break;
                    default:
                }
            }
        });
    }
    _validateNode(node) {
        if (node.element === undefined && node.type === 'root') {
            // root node does not have an element
            return [];
        }
        else if (node.element === undefined && node.type !== 'root') {
            throw new Error('Only root node element should be undefined');
        }
        const elementTag = node.element.tag;
        if (typeof elementTag !== 'string') {
            throw new Error('Element tag should be a string');
        }
        const elementDescription = dashboardElements.getDescription(elementTag);
        if (!elementDescription) {
            throw new Error(`Only available dashboard elements are allowed! Tag: ${elementTag}`);
        }
        return this.dashboardElementValidationService.validate(node.element, elementDescription);
    }
}
DashboardSanitizerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardSanitizerService, deps: [{ token: DashboardElementValidationService }], target: i0.ɵɵFactoryTarget.Injectable });
DashboardSanitizerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardSanitizerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardSanitizerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: DashboardElementValidationService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { DashboardBuilderServicesModule, DashboardManagementService, DashboardSanitizerService, DashboardStructureService };
